import type { GetDataForPanelDto } from "../../../types/api/map/map.types";
import type { MapSelection } from "../services/mapSelectionStorage";

/**
 * ID ТС из панели (`VehicleForPanel.id`), соответствующие текущему выбору в фильтре.
 * В `MapFiltersPanel` в storage попадает `v.number` как machineId — здесь сопоставляем с `id` из panelData.
 */
export function resolveSelectedVehicleEntityIdsFromPanel(
    panelData: GetDataForPanelDto | null | undefined,
    selection: MapSelection
): string[] | undefined {
    if (!selection.vehicle?.length) return undefined;

    const groups = panelData?.vehicleGroups ?? [];
    if (groups.length === 0) return undefined;

    const byCounterparty = new Map(
        groups.map((g) => [String(g.counterpartyVehicleId).trim(), g])
    );

    const ids: string[] = [];
    const seen = new Set<string>();

    for (const sel of selection.vehicle) {
        const cid = String(sel.contractorId).trim();
        if (!cid) continue;

        const group = byCounterparty.get(cid);
        const vehicles = group?.vehicles ?? [];
        if (vehicles.length === 0) continue;

        if (sel.showAll) {
            for (const v of vehicles) {
                const id = String(v.id).trim();
                if (id && !seen.has(id)) {
                    seen.add(id);
                    ids.push(id);
                }
            }
        } else {
            for (const mid of sel.machineIds ?? []) {
                const key = String(mid).trim();
                if (!key) continue;
                const vehicle = vehicles.find(
                    (x) => String(x.number).trim() === key || String(x.id).trim() === key
                );
                if (!vehicle) continue;
                const id = String(vehicle.id).trim();
                if (id && !seen.has(id)) {
                    seen.add(id);
                    ids.push(id);
                }
            }
        }
    }

    return ids.length > 0 ? ids : undefined;
}

/**
 * Строки для `subscribeToIds` — те же значения, что попадают в `machineIds` из панели (`VehicleForPanel.number`),
 * с учётом `showAll`. Если панель ещё не загружена, падаем обратно на «сырой» список из storage.
 */
export function buildMapVehicleSubscriptionIds(
    panelData: GetDataForPanelDto | null | undefined,
    selection: MapSelection
): string[] {
    const result: string[] = [];
    const seen = new Set<string>();
    const groups = panelData?.vehicleGroups ?? [];
    const byCounterparty = new Map(
        groups.map((g) => [String(g.counterpartyVehicleId).trim(), g])
    );

    for (const sel of selection.vehicle ?? []) {
        const cid = String(sel.contractorId).trim();
        if (!cid) continue;
        const group = byCounterparty.get(cid);
        const vehicles = group?.vehicles ?? [];

        const pushNumber = (raw: string) => {
            const n = String(raw).trim();
            if (!n || seen.has(n)) return;
            seen.add(n);
            result.push(n);
        };

        if (sel.showAll) {
            for (const v of vehicles) pushNumber(v.number);
        } else {
            for (const mid of sel.machineIds ?? []) pushNumber(String(mid));
        }
    }

    if (result.length === 0 && selection.vehicle?.length) {
        return selection.vehicle.flatMap((v) => v.machineIds ?? []);
    }

    return result;
}

/**
 * Номер ТС с панели по ключу из telemetry (`extId`, `id` или `number` в панели, либо числовой `transportId` строкой).
 */
export function resolveVehicleNumberForTransportPosition(
    panelData: GetDataForPanelDto | null | undefined,
    lookupKey: string
): string | null {
    if (!panelData?.vehicleGroups?.length) return null;
    const tid = String(lookupKey).trim();
    if (!tid) return null;
    const tidNum = Number(tid);
    const tidIsNum = Number.isFinite(tidNum);

    for (const g of panelData.vehicleGroups) {
        for (const v of g.vehicles ?? []) {
            const idStr = String(v.id).trim();
            if (idStr) {
                if (idStr === tid) {
                    const num = String(v.number).trim();
                    return num || idStr;
                }
                if (tidIsNum && Number(idStr) === tidNum) {
                    const num = String(v.number).trim();
                    return num || idStr;
                }
            }
            const numStr = String(v.number).trim();
            if (numStr) {
                if (numStr === tid) return numStr;
                if (tidIsNum && Number(numStr) === tidNum) return numStr;
            }
        }
    }
    return null;
}
